<template>
	<section
		class="text-white relative overflow-hidden"
		:style="{
			backgroundImage: `url('/img/knowledge-base-pattern.png')`,
			backgroundSize: '400px 400px',
			backgroundRepeat: 'repeat'
		}"
	>
		<div class="flex flex-col bg-gradient-to-r from-primary/90 to-primary-dark/90 px-4 pb-16 pt-24 xl:pt-16">
			<span
				class="text-white absolute end-0 top-0 m-4 h-14 w-14 cursor-pointer rounded-full border border-white/50 bg-slate-900/5 backdrop-blur-sm duration-300 hover:text-slate-900 hover:border-slate-900/50 hover:bg-white/10 max-md:self-end"
				@click="$backWithFallback($routeUrl.BlogUrl())"
			>
				<Icon name="mdi:arrow-left" class="h-full w-full rounded-full p-[15%]" />
			</span>
			<div class="relative w-full max-w-screen-xl self-center">
				<!-- Title -->
				<h1 class="mb-6 text-3xl font-bold leading-tight sm:text-4xl md:text-5xl">
					{{ $stringUtil.getLocalText(article?.title) || "~" }}
				</h1>

				<!-- Meta Info -->
				<div class="flex flex-wrap items-center gap-4 text-white/90">
					<!-- Author -->
					<div v-if="getUserDisplayName(article, 'post')" class="flex items-center gap-3">
						<BaseAvatar :target="article" type="post" class="h-12 w-12 backdrop-blur-sm" />
						<div>
							<div class="font-semibold">
								{{ getUserDisplayName(article, "post") }}
							</div>
							<div class="text-sm text-white/70">
								{{ getDateString(article?.published || article?.created) || "~" }}
							</div>
						</div>
					</div>

					<span class="text-white/50">•</span>

					<!-- Read Time / Duration -->
					<div v-if="readTimeText" class="flex items-center gap-2">
						<svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
						<span>{{ readTimeText }}</span>
					</div>

					<!-- Views -->
					<template v-if="article?.view_count">
						<span class="text-white/50">•</span>
						<div class="flex items-center gap-2">
							<svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
								/>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
								/>
							</svg>
							<span>
								{{ $t("n_views", { n: formatNumber(article?.view_count || 0) }, article?.view_count) }}
							</span>
						</div>
					</template>
				</div>

				<!-- Categories -->
				<div v-if="categories.length > 0" class="mt-6 flex flex-wrap gap-2">
					<span
						v-for="category in categories"
						:key="category.id"
						class="text-white rounded-full px-4 py-2 text-sm font-medium backdrop-blur-sm"
						:style="{ backgroundColor: category.color || 'rgba(255,255,255,0.2)' }"
					>
						{{ $stringUtil.getLocalText(category.title) || "~" }}
					</span>
				</div>
			</div>
		</div>
	</section>
</template>

<script lang="ts" setup>
const { t: $t } = useI18n()

import type { Post } from "~/models/post"
import { getUserDisplayName } from "~/models/user"

const categoryStore = useCategoryStore()
const app = useNuxtApp()
const moment = useMoment()

const props = defineProps<{
	article?: Post
}>()

const categories = computed(() => {
	return categoryStore.categories?.filter((c) => props.article?.category_ids?.includes?.(c.id)) || []
})

const getDateString = (d: any) => {
	if (!d) return "~"
	return moment.utc(d, "YYYY/MM/DD").local().format("jYYYY/jMM/jDD")
}

const formatNumber = (num: number): string => {
	if (num >= 1000000) {
		return (num / 1000000).toFixed(1) + "M"
	}
	if (num >= 1000) {
		return (num / 1000).toFixed(1) + "K"
	}
	return num.toString()
}

const readTimeText = computed(() => {
	if (!props.article) return ""
	return `${$t("read_time_n", {
		n: app.$i18n.t(
			"n_minutes",
			{
				n: Math.ceil((props.article.read_time || 1) / 60)
			},
			Math.ceil((props.article.read_time || 1) / 60)
		)
	})}`
})
</script>
